import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import AxiosApiCall from "../api/AxiosApiCall";
import API_URLS from "../api/ApiServices";
import { toast } from "react-toastify";

const axiosInstance = AxiosApiCall();

const STALE_5MIN = 1000 * 60 * 5;
const STALE_30MIN = 1000 * 60 * 30;

export const useHeadOffices = (filters = {}) => {
  const { start_date = "", end_date = "" } = filters;
  return useQuery({
    queryKey: ["headOffices", start_date, end_date],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (start_date) params.append("start_date", start_date);
      if (end_date) params.append("end_date", end_date);
      const url = `${API_URLS.getAllHeadOffices}${params.toString() ? `?${params.toString()}` : ""}`;
      const response = await axiosInstance.get(url);
      return response.data;
    },
    staleTime: STALE_5MIN,
  });
};

export const useHeadOfficeById = (headOfficeId) => {
  return useQuery({
    queryKey: ["headOffice", headOfficeId],
    queryFn: async () => {
      const response = await axiosInstance.get(`${API_URLS.getHeadOfficeById}?head_office_id=${headOfficeId}`);
      return response.data;
    },
    enabled: !!headOfficeId,
    staleTime: STALE_5MIN,
  });
};

export const useHeadOfficeDashboard = (headOfficeId, filters = {}) => {
  const { start_date = "", end_date = "" } = filters;
  return useQuery({
    queryKey: ["headOfficeDashboard", headOfficeId, start_date, end_date],
    queryFn: async () => {
      const params = new URLSearchParams({ head_office_id: headOfficeId });
      if (start_date) params.append("start_date", start_date);
      if (end_date) params.append("end_date", end_date);
      const response = await axiosInstance.get(`${API_URLS.getHeadOfficeDashboard}?${params.toString()}`);
      return response.data;
    },
    enabled: !!headOfficeId,
    staleTime: STALE_5MIN,
  });
};

export const useHeadOfficeInventory = (headOfficeId, perPage = 20) => {
  return useQuery({
    queryKey: ["headOfficeInventory", headOfficeId, perPage],
    queryFn: async () => {
      const response = await axiosInstance.get(
        `${API_URLS.getHeadOfficeInventory}?head_office_id=${headOfficeId}&per_page=${perPage}`
      );
      return response.data;
    },
    enabled: !!headOfficeId,
    staleTime: STALE_5MIN,
  });
};

export const useHeadOfficeBranches = (headOfficeId) => {
  return useQuery({
    queryKey: ["headOfficeBranches", headOfficeId],
    queryFn: async () => {
      const response = await axiosInstance.get(
        `${API_URLS.getHeadOfficeBranches}?head_office_id=${headOfficeId}`
      );
      return response.data;
    },
    enabled: !!headOfficeId,
    staleTime: STALE_5MIN,
  });
};

export const useCreateHeadOffice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const response = await axiosInstance.post(API_URLS.createHeadOffice, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["headOffices"] });
      toast.success("Head office created successfully!");
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || "Failed to create head office");
    },
  });
};

export const useUpdateHeadOffice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const response = await axiosInstance.put(API_URLS.updateHeadOffice, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["headOffices"] });
      queryClient.invalidateQueries({ queryKey: ["headOffice", variables.head_office_id] });
      toast.success("Head office updated successfully!");
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || "Failed to update head office");
    },
  });
};

export const useDeleteHeadOffice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (headOfficeId) => {
      const response = await axiosInstance.delete(API_URLS.deleteHeadOffice, {
        data: { head_office_id: headOfficeId },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["headOffices"] });
      toast.success("Head office deleted successfully!");
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || "Failed to delete head office");
    },
  });
};

export const useToggleHeadOfficeActivation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (headOfficeId) => {
      const response = await axiosInstance.patch(API_URLS.toggleHeadOfficeActivation, {
        head_office_id: headOfficeId,
      });
      return response.data;
    },
    onSuccess: (_, headOfficeId) => {
      queryClient.invalidateQueries({ queryKey: ["headOffices"] });
      queryClient.invalidateQueries({ queryKey: ["headOffice", headOfficeId] });
      toast.success("Head office activation status updated!");
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || "Failed to update activation status");
    },
  });
};

export const useToggleHeadOfficeStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (headOfficeId) => {
      const response = await axiosInstance.patch(API_URLS.toggleHeadOfficeStatus, {
        head_office_id: headOfficeId,
      });
      return response.data;
    },
    onSuccess: (_, headOfficeId) => {
      queryClient.invalidateQueries({ queryKey: ["headOffices"] });
      queryClient.invalidateQueries({ queryKey: ["headOffice", headOfficeId] });
      toast.success("Head office status updated!");
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || "Failed to update status");
    },
  });
};

export const useResetHeadOfficePassword = () => {
  return useMutation({
    mutationFn: async (headOfficeId) => {
      const response = await axiosInstance.post(API_URLS.resetHeadOfficePassword, {
        head_office_id: headOfficeId,
      });
      return response.data;
    },
    onSuccess: () => {
      toast.success("Password reset successfully! New password sent to email.");
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || "Failed to reset password");
    },
  });
};

export const useBranchProductUnits = (perPage = 1000) => {
  return useQuery({
    queryKey: ["branchProductUnits", perPage],
    queryFn: async () => {
      const response = await axiosInstance.post(`${API_URLS.getAllBranchProductUnit}`, { per_page: perPage });
      return response.data;
    },
    staleTime: STALE_30MIN,
  });
};

export const useAllocateStock = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const response = await axiosInstance.post(API_URLS.allocateStockToHeadOffice, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["headOfficeInventory", variables.head_office_id] });
      queryClient.invalidateQueries({ queryKey: ["headOfficeDashboard", variables.head_office_id] });
      toast.success("Stock allocated successfully!");
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || "Failed to allocate stock");
    },
  });
};
